// The Add contact picker on an opportunity: the account's contacts first, then
// anyone else who matches, labelled with their account.
import type { FilterOption } from '@olytic/foundation';

import { objectListUrl, type ObjectRow, parseObjectListResponse, relatedUrl } from '../objects/objectsApi';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

const ACCOUNT_LIMIT = 50;
const SEARCH_LIMIT = 20;

const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** A contact's name: as the related list names it, else first and last name, else the email. */
const contactLabel = (row: ObjectRow): string =>
  str(row.name) || [str(row.first_name), str(row.last_name)].filter(Boolean).join(' ') || str(row.email) || row.id;

const matches = (row: ObjectRow, query: string): boolean =>
  [row.name, row.email, row.title].some((v) => typeof v === 'string' && v.toLowerCase().includes(query));

/**
 * Options for the opportunity's contact picker. Empty, it lists the account's
 * contacts (or, when it has none, the first contacts of all); typed, the
 * account's matches come first, then other contacts with their account's name.
 */
export function contactOptionsFor(fetcher: Fetch, accountId: string | null) {
  return async (query: string): Promise<FilterOption[]> => {
    const q = query.trim().toLowerCase();
    const own = accountId
      ? (await parseObjectListResponse(await fetcher(relatedUrl('accounts', accountId, 'contacts', { limit: ACCOUNT_LIMIT, offset: 0 })))).rows
          .filter((row) => q === '' || matches(row, q))
          .map((row) => ({ value: row.id, label: contactLabel(row) }))
      : [];
    if (q === '' && own.length > 0) return own;
    const ownIds = new Set(own.map((o) => o.value));
    const others = (await parseObjectListResponse(await fetcher(objectListUrl('contacts', { q, limit: SEARCH_LIMIT, offset: 0 })))).rows
      .filter((row) => !ownIds.has(row.id))
      .map((row) => {
        const account = typeof row.account_name === 'string' && row.account_name !== '' ? row.account_name : 'No account';
        return { value: row.id, label: `${contactLabel(row)} · ${account}` };
      });
    return [...own, ...others];
  };
}
